/**
 * src/pages/public/ConsentSign.jsx — the client-facing signing page.
 *
 * No login, no account, no app. The client taps a link in an email and signs.
 * Everything this page needs comes from GET /api/consent/form/:token, and the
 * token in the URL is the only credential.
 *
 * The one piece of deliberate friction: the signature pad stays disabled until
 * the client has scrolled the terms to the bottom. A consent form nobody read
 * is worth very little if it is ever challenged, and "I couldn't sign until I
 * scrolled" is a far better record than a tick box.
 *
 * Three terminal states the page must handle gracefully, because they are what
 * a real client will hit: expired link, already signed, and success.
 *
 * REDESIGN.md Phase 4 — a firm boundary, not a judgement call (see audit.md
 * §7-8 and docs/redesign/progress.md): this pass restyles the page and adds
 * real accessibility polish for the shape that exists today (a labelled,
 * `aria-live` scroll-progress region; a clearly announced unlock state; a
 * properly labelled signature pad) — it does NOT invent discrete consent
 * questions the product doesn't have, and does NOT touch the signature
 * capture mechanism or what gets sent to `POST /api/consent/sign/:token`.
 * Both were explicitly flagged as needing Freddie's sign-off first, which
 * hasn't been given.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import SignaturePad from '../../components/SignaturePad.jsx';
import DownloadButton from '../../components/DownloadButton.jsx';
import { ErrorBanner, Loader } from '../../components/ui.jsx';
import { Button, Input, PortalHeader } from '../../components/ui/index.js';
import PortalFooter from './PortalFooter.jsx';
import { applyStudioAccent, clearStudioAccent } from '../../lib/studio-accent.js';
import './portal.css';

export default function ConsentSign() {
  const { token } = useParams();
  const { data, error, loading } = useApi(`/api/consent/form/${token}`);

  const padRef = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [healthConsent, setHealthConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  const scrollRegionId = useId();

  // The studio's accent colour themes this page — it is the studio's form,
  // not ours. See lib/studio-accent.js for why this sets a raw token rather
  // than --accent directly.
  useEffect(() => {
    applyStudioAccent(data?.studio?.accentColor);
    return clearStudioAccent;
  }, [data?.studio?.accentColor]);

  // Prefill the name from the client record, since we already know it.
  useEffect(() => {
    if (data?.client?.name) setSignerName((current) => current || data.client.name);
  }, [data?.client?.name]);

  /**
   * Enables the signature pad once the terms have been read to the bottom.
   *
   * The 8px tolerance covers fractional scroll heights on high-DPI screens,
   * where scrollTop + clientHeight never quite equals scrollHeight.
   *
   * @param {React.UIEvent<HTMLDivElement>} e
   */
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setScrolledToEnd(true);
  };

  // A short form on a big screen may not scroll at all; measure once it is
  // rendered and unlock immediately if there is nothing to scroll.
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setScrolledToEnd(true);
  }, [data?.consentText]);

  const submit = async (e) => {
    e.preventDefault();
    const signatureDataUrl = padRef.current?.toDataURL();
    if (!signatureDataUrl) {
      setSubmitError('Please draw your signature.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.post(`/api/consent/sign/${token}`, {
        signerName: signerName.trim(),
        signatureDataUrl,
        healthConsent,
      });
      setResult(res);
    } catch (err) {
      setSubmitError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-page">
      <main className="portal-main">
        <Loader loading={loading} error={error}>
          {data ? (
            <>
              <PortalHeader studioName={data.studio.name} />
              {data.studio.logoUrl ? (
                <div style={{ textAlign: 'center', marginTop: -16, marginBottom: 16 }}>
                  <img src={data.studio.logoUrl} alt="" style={{ maxHeight: 56 }} />
                </div>
              ) : null}

              {result ? (
                <SignedConfirmation pdfPath={result.pdfPath} time={data.appointment.formattedTime} />
              ) : data.status === 'expired' ? (
                <ExpiredNotice contactEmail={data.studio.contactEmail} />
              ) : data.status === 'signed' ? (
                <SignedConfirmation pdfPath={data.pdfPath} time={data.appointment.formattedTime} already />
              ) : (
                <form className="stack-lg" onSubmit={submit}>
                  <section className="portal-panel">
                    <h2 className="portal-panel-title" style={{ marginBottom: 4 }}>
                      Your appointment
                    </h2>
                    <p className="portal-lead" style={{ margin: 0 }}>
                      {data.appointment.formattedTime}
                      {data.artist.name ? ` with ${data.artist.name}` : ''}
                    </p>
                  </section>

                  <section className="portal-panel portal-panel--current">
                    <span className="portal-reg-mark portal-reg-mark--tl" aria-hidden="true" />
                    <span className="portal-reg-mark portal-reg-mark--tr" aria-hidden="true" />
                    <h2 className="portal-panel-title" id={`${scrollRegionId}-label`}>
                      Please read this in full
                    </h2>
                    {/*
                      role="region" + aria-labelledby: a scrollable block of
                      text is otherwise invisible to a screen reader as
                      "a thing with more content than is announced" — this
                      names it and puts it in the tab order (tabIndex=0) so
                      keyboard/AT users can reach and scroll it without a
                      pointer. onScroll behaviour is unchanged from before.
                    */}
                    <div
                      className="portal-consent-scroll"
                      ref={scrollRef}
                      onScroll={onScroll}
                      role="region"
                      aria-labelledby={`${scrollRegionId}-label`}
                      tabIndex={0}
                    >
                      {data.consentText}
                    </div>
                    {/*
                      aria-live="polite": the unlock is a real state change a
                      screen-reader user needs to hear once, the moment it
                      happens — not on every scroll tick, which is why the
                      text itself (not a separate announcer node) carries the
                      live region: React only touches the DOM when the
                      rendered string actually changes.
                    */}
                    <p
                      className={`portal-consent-progress${scrolledToEnd ? ' portal-consent-progress--unlocked' : ''}`}
                      aria-live="polite"
                    >
                      {scrolledToEnd
                        ? '✓ Read to the end — you can sign below.'
                        : 'Scroll to the end of the form to continue.'}
                    </p>
                  </section>

                  <section className="portal-panel">
                    {/*
                      Explicit consent for health information, kept separate
                      from the form above and unticked by default: UK GDPR
                      needs a clear, specific "yes" to process health data,
                      not one buried in the terms. The wording comes from the
                      API so it matches what's printed on the signed PDF, and
                      the API refuses to sign without it.
                    */}
                    <label className="portal-health-consent">
                      <input
                        type="checkbox"
                        checked={healthConsent}
                        onChange={(e) => setHealthConsent(e.target.checked)}
                        required
                      />
                      <span>{data.healthConsentStatement}</span>
                    </label>

                    <Input
                      id="signerName"
                      label="Your full name"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      required
                      minLength={2}
                      autoComplete="name"
                    />

                    <div style={{ marginTop: 16 }}>
                      <label htmlFor="signature">
                        Signature — draw with your finger, stylus or mouse
                      </label>
                      <SignaturePad
                        ref={padRef}
                        disabled={!scrolledToEnd}
                        onChange={setHasInk}
                      />
                    </div>

                    <ErrorBanner>{submitError}</ErrorBanner>

                    <Button
                      type="submit"
                      variant="primary"
                      style={{ marginTop: 16 }}
                      disabled={
                        submitting || !scrolledToEnd || !hasInk || !healthConsent || signerName.trim().length < 2
                      }
                    >
                      {submitting ? 'Submitting…' : 'Sign and submit'}
                    </Button>

                    <p className="portal-terms" style={{ marginBottom: 0 }}>
                      By signing you confirm the statements above are true. We record the time and your
                      IP address alongside your signature, and store a signed PDF copy.
                    </p>
                  </section>
                </form>
              )}
            </>
          ) : null}
        </Loader>
      </main>
      <PortalFooter />
    </div>
  );
}

/**
 * Terminal state: signed.
 *
 * The download button only appears while the API offers a `pdfPath` — for 24
 * hours after signing. After that the emailed attachment is the client's copy.
 *
 * @param {{ pdfPath: string|null, time: string, already?: boolean }} props
 * @returns {JSX.Element}
 */
function SignedConfirmation({ pdfPath, time, already = false }) {
  return (
    <div className="portal-panel" style={{ textAlign: 'center' }}>
      <h2 className="portal-panel-title">{already ? 'This form is already signed' : 'All done — thank you'}</h2>
      <p>
        {already
          ? 'Nothing more to do. We have your consent form on file.'
          : `Your consent form is signed and on file. See you on ${time}.`}
      </p>
      {pdfPath ? (
        <p>
          <DownloadButton path={pdfPath} filename="consent-form.pdf" className="btn btn-primary">
            Download your copy
          </DownloadButton>
        </p>
      ) : null}
      <p className="portal-lead">We have also emailed you a copy.</p>
    </div>
  );
}

/**
 * Terminal state: link expired.
 *
 * @param {{ contactEmail: string }} props
 * @returns {JSX.Element}
 */
function ExpiredNotice({ contactEmail }) {
  return (
    <div className="portal-panel" style={{ textAlign: 'center' }}>
      <h2 className="portal-panel-title">This link has expired</h2>
      <p>
        Consent links are only valid for 48 hours. Ask the studio to send you a new one — it takes
        them a couple of seconds.
      </p>
      <p>
        <a className="btn" href={`mailto:${contactEmail}`}>
          Email {contactEmail}
        </a>
      </p>
    </div>
  );
}
